import { computed, ref } from 'vue';
import { isEqual } from 'lodash-es';
import { storeToRefs } from 'pinia';
import { useAppAuthData } from '@/stores/app/authData';
import { useFilters } from '@/stores/filters/filters';
import { useJobArchitecture } from '@/stores/jobArchitecture/jobArchitecture';
import { useTracking } from '@/stores/tracking/tracking';
import {
  computeArchitectureAlerts,
  getMatchingAlertCodes,
} from '@/components/jobArchitecture/alertHelpers';
import {
  collectExpandedNodeIdsForAlertFilter,
  pruneSelectedProfileUlidsToVisibleTree,
} from '@/components/jobArchitecture/helpers';
import {
  applyFilterSnapshotToFields,
  buildFilteredTree,
  deriveFilterFields,
  getSelectedAlertCategoryIds,
  mergeFilterFieldOptions,
  mergeJobArchitectureRouteQuery,
} from './architectureFilterUtils';

export {
  JOB_ARCHITECTURE_ALERTS_FILTER_ID,
  JOB_ARCHITECTURE_GROUP_FILTER_ID,
  JOB_ARCHITECTURE_FAMILY_FILTER_ID,
  JOB_ARCHITECTURE_PROFILE_FILTER_ID,
  JOB_ARCHITECTURE_JOB_LEVEL_FILTER_ID,
  JOB_ARCHITECTURE_JOB_CODE_FILTER_ID,
  JOB_ARCHITECTURE_FILTER_QUERY_KEYS,
  mergeJobArchitectureRouteQuery,
  mergeFilterFieldOptions,
  applyFilterSnapshotToFields,
  applySearchFilter,
  deriveFilterFields,
  applyClientFilters,
  getSelectedAlertCategoryIds,
  buildFilteredTree,
} from './architectureFilterUtils';

const architectureSearchText = ref('');

const JOB_ARCHITECTURE_TRACKING_FILTER_LABELS = {
  'Architecture Alerts': 'Alert',
};

// Client-side Job Architecture filtering composable using the shared `useFilters` store
export const useJobArchitectureFilters = () => {
  const { fields, tempFilters } = storeToRefs(useFilters());
  const { treeData } = storeToRefs(useJobArchitecture());
  const { isStandaloneJobNav } = storeToRefs(useAppAuthData());

  const { updateFilters, setFilterFields } = useFilters();
  const {
    getAndClearListFilterSnapshot,
    saveListFilterSnapshot,
    setSelectedProfileUlids,
  } = useJobArchitecture();
  const { selectedProfileUlids } = storeToRefs(useJobArchitecture());

  const filteredTreeData = computed(() => buildFilteredTree(
    treeData.value,
    fields.value,
    architectureSearchText.value,
    { skipEmployeeAlerts: isStandaloneJobNav.value },
  ));

  const selectedAlertCategoryIds = computed(() => getSelectedAlertCategoryIds(fields.value));

  const getAlertFilterExpansionIds = () => {
    const alertCategoryIds = selectedAlertCategoryIds.value;

    if (!alertCategoryIds.length) {
      return null;
    }

    const filtered = filteredTreeData.value;

    if (!filtered?.groups?.length) {
      return new Set();
    }

    const matchingCodes = getMatchingAlertCodes(alertCategoryIds);

    return collectExpandedNodeIdsForAlertFilter(
      filtered,
      computeArchitectureAlerts(filtered, { skipEmployeeAlerts: isStandaloneJobNav.value }),
      matchingCodes,
    );
  };

  // Update filter options from the filtered data
  const refreshFilterOptions = () => {
    const filtered = filteredTreeData.value;

    if (!filtered) {
      return;
    }

    const optionsForVisibleTable = deriveFilterFields(filtered, {
      skipEmployeeAlerts: isStandaloneJobNav.value,
    });

    fields.value = mergeFilterFieldOptions(fields.value, optionsForVisibleTable);
    tempFilters.value = mergeFilterFieldOptions(tempFilters.value, optionsForVisibleTable);
  };

  const setSearchText = (value) => {
    architectureSearchText.value = value || '';
  };

  const clearSearch = () => {
    architectureSearchText.value = '';
  };

  const pruneSelectionToFilteredTree = () => {
    const pruned = pruneSelectedProfileUlidsToVisibleTree(
      selectedProfileUlids.value,
      filteredTreeData.value,
    );

    if (pruned.length !== selectedProfileUlids.value.length) {
      setSelectedProfileUlids(pruned);
    }
  };

  // Populate filter fields from full `treeData`, snapshot, or URL query.
  // Returns true when filters were restored from a compare-navigation snapshot.
  const initializeFilters = (routeQuery = {}) => {
    const tree = treeData.value;

    if (!tree) {
      return false;
    }

    const snapshot = getAndClearListFilterSnapshot();

    if (snapshot) {
      setSearchText(snapshot.searchText);

      const filterFields = applyFilterSnapshotToFields(
        deriveFilterFields(tree, { skipEmployeeAlerts: isStandaloneJobNav.value }),
        snapshot.fields,
      );

      updateFilters({ filters: filterFields });
      refreshFilterOptions();
      pruneSelectionToFilteredTree();

      return true;
    }

    if (routeQuery.q) {
      setSearchText(routeQuery.q);
    }

    const filterFields = deriveFilterFields(tree, {
      skipEmployeeAlerts: isStandaloneJobNav.value,
    }).map((field) => {
      const queryValue = routeQuery[field.id];

      if (queryValue == null || queryValue === '') {
        return field;
      }

      const ids = Array.isArray(queryValue) ? queryValue : [queryValue];
      const value = field.options.filter((option) => ids.includes(option.id));

      return { ...field, value };
    });

    updateFilters({ filters: filterFields });
    refreshFilterOptions();
    pruneSelectionToFilteredTree();

    return false;
  };

  // Copy `tempFilters` into applied `fields` via `setFilterFields`, then narrow options like a filter API response.
  const trackAppliedSidebarFilters = () => {
    fields.value.forEach((field, index) => {
      if (!isEqual(field.value, tempFilters.value[index]?.value)) {
        const filterName = JOB_ARCHITECTURE_TRACKING_FILTER_LABELS[field.label] || field.label;

        useTracking().jaListAppliedSidebarFilter({ filterName });
      }
    });
  };

  const syncAppliedState = () => {
    trackAppliedSidebarFilters();

    setFilterFields(tempFilters.value.map((field) => ({ ...field })));

    refreshFilterOptions();
  };

  const reloadPage = ({
    router,
    routeQuery,
    mergedTempFilters,
    applySidebarFilters = false,
  }) => {
    if (applySidebarFilters) {
      syncAppliedState();
    } else {
      refreshFilterOptions();
    }

    pruneSelectionToFilteredTree();

    router.push({
      query: mergeJobArchitectureRouteQuery(
        routeQuery,
        mergedTempFilters,
        architectureSearchText.value,
      ),
    }).catch(() => true);
  };

  const saveListFilterSnapshotForCompare = () => {
    saveListFilterSnapshot({
      fields: fields.value,
      searchText: architectureSearchText.value,
    });
  };

  return {
    initializeFilters,
    syncAppliedState,
    refreshFilterOptions,
    reloadPage,
    saveListFilterSnapshotForCompare,
    filteredTreeData,
    selectedAlertCategoryIds,
    getAlertFilterExpansionIds,
    searchText: architectureSearchText,
    setSearchText,
    clearSearch,
  };
};
